<template>
  <div class="flex flex-col" :style="{ height: containerHeight, transition: 'height 0.3s ease' }">
    <!-- Quick Link Tabs -->
    <div class="mb-3">
      <FilterTabs
        label="View by"
        v-model="activeQuickLink"
        :options="quickLinkOptions"
        @change="selectQuickLink"
      />
    </div>

    <div class="flex gap-5 flex-1 min-h-0">
      <!-- Left Panel: Navigation Path (hidden when at root "Browse by Type") -->
      <div v-if="!showEntityTypeList" class="w-48 flex flex-col overflow-y-auto shrink-0 gap-1.5">
        <!-- Root card -->
        <div
          @click="onRootClick"
          class="px-3 py-2 rounded-lg cursor-pointer transition-colors border-l-[3px]"
          :class="(!selectedEntityType && navigationPath.length === 0)
            ? 'bg-primary-50 border-primary-500'
            : 'bg-gray-50 border-transparent'"
        >
          <div class="flex items-center gap-2">
            <span class="text-xs text-gray-400">▲</span>
            <span class="text-xs font-medium"
              :class="(!selectedEntityType && navigationPath.length === 0) ? 'text-primary-500' : 'text-growth-800'"
            >
              {{ activeQuickLink === 'my' ? 'My Practices' : 'All Entities' }}
            </span>
          </div>
        </div>

        <!-- Entity Type card (All Entities mode only) -->
        <div
          v-if="selectedEntityType && activeQuickLink === 'all'"
          @click="navigateToType(selectedEntityType)"
          class="px-3 py-2 rounded-lg cursor-pointer transition-colors border-l-[3px]"
          :class="navigationPath.length === 0
            ? 'bg-primary-50 border-primary-500'
            : 'bg-gray-50 border-transparent'"
        >
          <div class="text-xs font-medium truncate" :class="navigationPath.length === 0 ? 'text-primary-500' : 'text-growth-800'">
            {{ selectedEntityType }}
          </div>
        </div>

        <!-- Navigation Path cards -->
        <div
          v-for="(item, idx) in navigationPath"
          :key="item.id"
          @click="navigateToPathIndex(idx)"
          class="px-3 py-2 rounded-lg cursor-pointer transition-colors border-l-[3px]"
          :class="idx === navigationPath.length - 1
            ? 'bg-primary-50 border-primary-500'
            : 'bg-gray-50 border-transparent'"
        >
          <div class="flex items-center justify-between gap-1">
            <div class="min-w-0">
              <div class="sensitive text-xs font-medium truncate" :class="idx === navigationPath.length - 1 ? 'text-primary-500' : 'text-growth-800'">
                {{ item.name }}
              </div>
              <div class="text-[11px] truncate text-gray-400">{{ item.entity_type }}</div>
            </div>
            <router-link :to="`/entity/${item.id}`" @click.stop class="text-md hover:underline text-neutral-500 shrink-0 mr-1" title="View entity">
              ↗
            </router-link>
          </div>
        </div>
      </div>

      <!-- Right Panel -->
      <div class="flex-1 flex flex-col min-w-0 border border-growth-50 rounded-lg bg-white">
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-3 gap-4 border-b border-growth-50">
          <template v-if="showEntityTypeList">
            <div class="text-xs font-medium text-growth-800">Browse by Type</div>
            <div class="text-[11px] text-growth-600">{{ entityTypes.length }} types</div>
          </template>
          <template v-else>
            <div class="flex items-center gap-2">
              <input
                v-if="show_search"
                v-model="searchQuery"
                type="text"
                placeholder="Search..."
                class="border border-growth-50 rounded-lg px-3 py-1.5 text-xs w-48 text-growth-800 focus:outline-none focus:ring-2"
              />
              <GroupedInfoTagFilter
                v-model="infoTagGroups"
                v-model:groupsMode="infoTagGroupsMode"
                label="Filter by Tag"
                buttonLabel="Tag"
                color="emerald"
                direction="horizontal"
              />
            </div>
            <div class="flex items-center gap-2">
              <div class="text-[11px] whitespace-nowrap text-growth-600">
                {{ filteredChildren.length }} entities
                <span v-if="hasActiveTagFilter && filteredChildren.length !== childrenBeforeTagFilter.length"
                  class="text-growth-400">/ {{ childrenBeforeTagFilter.length }}</span>
              </div>
              <button
                @click="showTagCoverage = !showTagCoverage"
                class="p-1 rounded transition-colors"
                :class="showTagCoverage ? 'text-primary-500 bg-primary-50' : 'text-growth-400 hover:text-growth-600 hover:bg-gray-50'"
                title="Toggle tag coverage chart"
              >
                <svg class="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M3 17V3" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/>
                  <path d="M3 17h14" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/>
                  <rect x="5.5" y="9" width="2.5" height="7" rx="0.5" />
                  <rect x="9.5" y="5" width="2.5" height="11" rx="0.5" />
                  <rect x="13.5" y="7.5" width="2.5" height="8.5" rx="0.5" />
                </svg>
              </button>
            </div>
          </template>
        </div>

        <!-- Content Area -->
        <div class="flex-1 overflow-y-auto">
          <LoadingIndicator v-if="loading" class="p-6" message="Loading entities..." />

          <!-- Entity Type List (root of All Entities) -->
          <template v-else-if="showEntityTypeList">
            <div
              v-for="et in entityTypes"
              :key="et.type_id"
              @click="navigateToType(et.type_name)"
              class="px-4 py-2.5 cursor-pointer flex items-center gap-2 hover:bg-gray-50 border-b border-growth-50"
            >
              <span class="text-xs text-growth-800">{{ et.type_name }}</span>
              <span class="text-[11px] h-5 px-1.5 rounded-full inline-flex items-center justify-center gap-0.5 text-primary-600 bg-primary-50 border border-primary-300">{{ et.entity_count?.toLocaleString() || 0 }}→</span>
            </div>
          </template>

          <!-- Entity List -->
          <template v-else>
            <table class="w-full text-xs">
              <thead class="text-left border-b border-growth-50 text-growth-600 sticky top-0 z-[1] bg-white">
                <tr>
                  <th @click="toggleSort('name')" class="py-2 px-4 font-medium cursor-pointer select-none hover:text-gray-900 w-[24%]">
                    <span class="inline-flex items-center gap-1">Name <span class="text-[11px]">{{ sortIndicator('name') }}</span></span>
                  </th>
                  <th v-if="showColumn('tags')" class="py-2 px-2 font-medium w-[16%]">Tags</th>
                  <th v-if="showColumn('last_contact')" @click="toggleSort('last_touch')" class="py-2 px-2 font-medium text-right cursor-pointer select-none hover:text-gray-900 w-[20%]">
                    <span class="inline-flex items-center gap-1 justify-end">Last Touch <span class="text-[11px]">{{ sortIndicator('last_touch') }}</span></span>
                  </th>
                  <th v-if="showColumn('score')" @click="toggleSort('score')" class="py-2 px-2 font-medium text-center cursor-pointer select-none hover:text-gray-900 w-[10%]">
                    <span class="inline-flex items-center gap-1 justify-center">Score <span class="text-[11px]">{{ sortIndicator('score') }}</span></span>
                  </th>
                  <th v-if="showColumn('attribution')" @click="toggleSort('attribution')" class="py-2 px-2 font-medium text-right cursor-pointer select-none hover:text-gray-900 w-[15%]">
                    <span class="inline-flex items-center gap-1 justify-end">Attr <span class="text-[11px]">{{ sortIndicator('attribution') }}</span></span>
                  </th>
                  <th @click="toggleSort('type')" class="py-2 px-2 font-medium cursor-pointer select-none hover:text-gray-900 w-[16%]">
                    <span class="inline-flex items-center gap-1">Type <span class="text-[11px]">{{ sortIndicator('type') }}</span></span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in filteredChildren"
                  :key="item.id"
                  @click="goToEntityPage(item.id)"
                  class="cursor-pointer hover:bg-gray-50 border-b border-growth-50"
                >
                  <td class="py-3 px-4 text-growth-800">
                    <div class="flex items-center gap-2">
                      <span class="sensitive font-medium text-blue-600 hover:underline">{{ item.name }}</span>
                      <span
                        v-if="item.childCount > 0"
                        @click.stop="handleEntityClick(item)"
                        class="text-[11px] h-5 px-1.5 rounded-full inline-flex items-center justify-center gap-0.5 text-primary-600 bg-primary-50 border border-primary-300 cursor-pointer hover:bg-primary-100 transition-colors"
                      >
                        {{ item.childCount }}→
                      </span>
                    </div>
                  </td>
                  <td v-if="showColumn('tags')" class="py-3 px-2">
                    <div class="flex flex-wrap gap-1 items-center">
                      <span
                        v-for="tag in displayTags(item)"
                        :key="tag.name"
                        class="inline-flex items-center gap-1.5 text-xs leading-snug px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-700 max-w-[160px]"
                        :title="`${tag.type}: ${tag.name}`"
                      >
                        <span class="w-1.5 h-1.5 rounded-full shrink-0" :style="{ backgroundColor: tag.color || '#6b7280' }"></span>
                        <span class="truncate">{{ tag.name }}</span>
                      </span>
                      <span v-if="allTags(item).length > 3" class="text-[11px] text-growth-600 ml-1">+{{ allTags(item).length - 3 }}</span>
                    </div>
                  </td>
                  <td v-if="showColumn('last_contact')" class="py-2 px-2 text-right text-xs">
                    <div class="text-growth-800">
                      <span class="text-growth-400">Last Touch: </span>
                      <span>{{ formatDateCompact(item.last_engagement_date) }}</span>
                    </div>
                    <div class="text-growth-400">
                      <span>Last In-Person: </span>
                      <span>{{ formatDateCompact(item.last_in_person_date) }}</span>
                    </div>
                  </td>
                  <td v-if="showColumn('score')" class="py-3 px-2 text-center">
                    <CC_Engagement_Score_Badge :params="{ value: item.engagement_score }" />
                  </td>
                  <td v-if="showColumn('attribution')" class="py-3 px-2 text-right text-growth-800">
                    {{ item.attribution?.total_attribution?.toLocaleString() || '0' }}
                  </td>
                  <td class="py-3 px-2 text-xs text-growth-600">{{ item.entity_type }}</td>
                </tr>
              </tbody>
            </table>
            <div v-if="!loading && filteredChildren.length === 0" class="text-center py-8 text-growth-600">
              No entities found
            </div>
          </template>
        </div>

        <!-- Tag Coverage Chart -->
        <EntityTagCoverage
          v-if="showTagCoverage && !showEntityTypeList"
          :allEntities="childrenBeforeTagFilter"
          :tagGroups="activeTagGroups"
          :groupsMode="infoTagGroupsMode"
          :infoTagLookup="infoTagLookup"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import GroupedInfoTagFilter from '@/components/widgetcomponents/Dropdown_Selector/GroupedInfoTagFilter.vue'
import CC_Engagement_Score_Badge from '@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Engagement_Score_Badge.vue'
import FilterTabs from '@/components/widgetcomponents/library/FilterTabs.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'
import EntityTagCoverage from '@/components/ConfigurablePageComponents/Engage/EntityTagCoverage.vue'
import Tag from '@/components/widgetcomponents/library/Tag.vue'
import { canAccessFeature } from '@/utils/access';
import { FEATURES } from '@/constants/features';

export default {
  name: 'EntityHierarchyNavigatorV2',
  components: { GroupedInfoTagFilter, FilterTabs, LoadingIndicator, CC_Engagement_Score_Badge, EntityTagCoverage, Tag },
  props: {
    localStorage_key: { type: String, default: 'entity_hierarchy_nav_v2' },
    columns: { type: Array, default: () => ['tags', 'last_contact', 'score', 'attribution'] },
    show_search: { type: Boolean, default: true },
    excluded_entity_types: { type: Array, default: () => [] }
  },
  data() {
    return {
      activeQuickLink: 'all',
      selectedEntityType: null,
      navigationPath: [],
      entityTypes: [],
      children: [],
      linkedEntity: null,
      loading: false,
      searchQuery: '',
      sortColumn: 'name',
      sortDirection: 'asc',
      infoTagGroups: [],
      infoTagGroupsMode: 'or',
      showTagCoverage: false,
      filterTagCache: {}
    }
  },
  computed: {
    quickLinkOptions() {
      return [
        ...(canAccessFeature(FEATURES.COMPONENT_PRACTICE_PARTNER_ALL)
          ? [{ value: 'all', label: 'All Entities' }]
          : []),
        { value: 'my', label: 'My Practices' }
      ]
    },
    containerHeight() {
      const base = this.showTagCoverage ? 640 : 420
      return base + 'px'
    },
    showEntityTypeList() {
      return this.activeQuickLink === 'all' && !this.selectedEntityType && this.navigationPath.length === 0
    },
    childrenBeforeTagFilter() {
      let result = this.children
      if (this.excluded_entity_types.length > 0) {
        result = result.filter(e => !this.excluded_entity_types.includes(e.entity_type))
      }
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase()
        result = result.filter(c => c.name.toLowerCase().includes(q))
      }
      return result
    },
    infoTagLookup() {
      const lookup = { ...this.filterTagCache }
      for (const entity of this.children) {
        for (const tag of (entity.tags || [])) {
          if (tag.id) {
            lookup[tag.id] = { id: tag.id, name: tag.name, type: tag.type, color: tag.color }
          }
        }
      }
      return lookup
    },
    activeTagGroups() {
      return (this.infoTagGroups || []).filter(g =>
        (g.include?.length || 0) > 0 || (g.exclude?.length || 0) > 0
      )
    },
    hasActiveTagFilter() {
      return this.activeTagGroups.length > 0
    },
    filteredChildren() {
      let result = this.childrenBeforeTagFilter

      if (this.hasActiveTagFilter) {
        const groups = this.activeTagGroups
        const groupsMode = this.infoTagGroupsMode === 'and' ? 'and' : 'or'
        result = result.filter(e => {
          const tagNames = new Set((e.tags || []).map(t => t.name))
          const tagIds = new Set((e.tags || []).map(t => t.id))
          const groupMatches = groups.map(g => {
            const include = g.include || []
            const exclude = g.exclude || []
            const mode = g.mode === 'and' ? 'and' : 'or'
            const incOk = include.length === 0
              ? true
              : (mode === 'and' ? include.every(tid => tagIds.has(tid)) : include.some(tid => tagIds.has(tid)))
            const excOk = !exclude.some(tid => tagIds.has(tid))
            return incOk && excOk
          })
          return groupsMode === 'and' ? groupMatches.every(Boolean) : groupMatches.some(Boolean)
        })
      }

      const dir = this.sortDirection === 'asc' ? 1 : -1
      return result.sort((a, b) => {
        const va = this.getSortValue(a)
        const vb = this.getSortValue(b)
        if (va == null && vb == null) return 0
        if (va == null) return 1
        if (vb == null) return -1
        if (typeof va === 'string') return dir * va.localeCompare(vb)
        return dir * (va - vb)
      })
    }
  },
  methods: {
    showColumn(col) {
      return this.columns.includes(col)
    },

    toggleSort(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = column
        this.sortDirection = 'asc'
      }
    },

    sortIndicator(column) {
      if (this.sortColumn !== column) return ''
      return this.sortDirection === 'asc' ? '▲' : '▼'
    },

    getSortValue(item) {
      switch (this.sortColumn) {
        case 'name': return item.name || ''
        case 'last_touch': return item.last_engagement_date || null
        case 'score': return item.engagement_score?.composite ?? null
        case 'attribution': return item.attribution?.total_attribution || 0
        case 'type': return item.entity_type || ''
        default: return item.name || ''
      }
    },

    async selectQuickLink(key) {
      localStorage.setItem(this.localStorage_key + '_quick', key)
      if (key === 'my') {
        await this.navigateToMyPractices()
      } else {
        await this.navigateToRoot()
      }
    },

    async onRootClick() {
      if (this.activeQuickLink === 'my') {
        await this.navigateToMyPractices()
      } else {
        await this.navigateToRoot()
      }
    },

    saveNavState() {
      const state = {
        selectedEntityType: this.selectedEntityType,
        navigationPath: this.navigationPath
      }
      localStorage.setItem(this.localStorage_key + '_nav', JSON.stringify(state))
    },

    async navigateToRoot() {
      this.selectedEntityType = null
      this.navigationPath = []
      this.children = []
      this.searchQuery = ''
      this.infoTagGroups = []
      this.infoTagGroupsMode = 'or'
      this.saveNavState()
    },

    async navigateToType(typeName) {
      this.loading = true
      this.selectedEntityType = typeName
      this.navigationPath = []
      this.searchQuery = ''
      await this.fetchChildren(null, typeName)
      this.saveNavState()
      this.loading = false
    },

    async navigateToPathIndex(idx) {
      const item = this.navigationPath[idx]
      this.navigationPath = this.navigationPath.slice(0, idx + 1)
      this.loading = true
      await this.fetchChildren(item.id)
      this.saveNavState()
      this.loading = false
    },

    async navigateToMyPractices() {
      this.loading = true
      this.selectedEntityType = null
      this.navigationPath = []

      if (!this.linkedEntity) {
        await this.fetchLinkedEntity()
      }

      if (this.linkedEntity) {
        this.navigationPath = [{
          id: this.linkedEntity.id,
          name: this.linkedEntity.name,
          entity_type: this.linkedEntity.entity_type_name || this.linkedEntity.entity_type
        }]
        await this.fetchChildren(this.linkedEntity.id)
      }
      this.saveNavState()
      this.loading = false
    },

    async handleEntityClick(item) {
      if (item.childCount > 0) {
        this.navigationPath = [...this.navigationPath, {
          id: item.id,
          name: item.name,
          entity_type: item.entity_type
        }]
        this.loading = true
        await this.fetchChildren(item.id)
        this.saveNavState()
        this.loading = false
      } else {
        this.goToEntityPage(item.id)
      }
    },

    async fetchChildren(entityId, entityTypeName = null) {
      try {
        const params = {}
        if (entityId) {
          params.root_entity_id = entityId
          params.navigation_path_ids = JSON.stringify(this.navigationPath.map(item => item.id))
        } else if (entityTypeName) {
          params.entity_type_name = entityTypeName
        }

        const response = await axios.get('/api/entities/entity-navigator/', { params })
        this.children = response.data
      } catch (error) {
        console.error('Error fetching children:', error)
        this.children = []
      }
    },

    async fetchLinkedEntity() {
      try {
        const response = await axios.get('/api/entities/', {
          params: { linked_user_id: 'me' }
        })
        const results = response.data.results || response.data
        if (results.length > 0) {
          this.linkedEntity = results[0]
        }
      } catch (error) {
        console.error('Error fetching linked entity:', error)
        this.linkedEntity = null
      }
    },

    async fetchEntityTypes() {
      try {
        const response = await axios.get('/api/entities/entity-navigator/')
        this.entityTypes = response.data
          .filter(t => !this.excluded_entity_types.includes(t.type_name))
          .sort((a, b) => a.type_name.localeCompare(b.type_name))
      } catch (error) {
        console.error('Error fetching entity types:', error)
        this.entityTypes = []
      }
    },

    async fetchAllInfoTags() {
      try {
        const [tagsRes, typesRes] = await Promise.all([
          axios.get('/api/infotags/'),
          axios.get('/api/infotagtypes/')
        ])
        const typeNameMap = {}
        for (const t of (typesRes.data || [])) typeNameMap[t.id] = t.name
        const cache = {}
        for (const tag of (tagsRes.data || [])) {
          cache[tag.id] = { id: tag.id, name: tag.name, type: typeNameMap[tag.infoTagType] || 'Other', color: null }
        }
        this.filterTagCache = cache
      } catch (error) {
        console.error('Error fetching info tags:', error)
      }
    },

    goToEntityPage(id) {
      this.$router.push({ path: `/entity/${id}` })
    },

    allTags(item) {
      return item.tags || []
    },

    displayTags(item) {
      return this.allTags(item).slice(0, 3)
    },

    formatDateCompact(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
      const days = Math.floor((new Date() - date) / 86400000)
      return `${months[date.getMonth()]} ${date.getDate()} (${days}d)`
    }
  },
  async mounted() {
    this.loading = true
    try {
      this.fetchAllInfoTags()
      await this.fetchEntityTypes()

      this.activeQuickLink = this.quickLinkOptions[0]?.value || 'all'

      const savedQuick = localStorage.getItem(this.localStorage_key + '_quick')
      if (savedQuick === 'my') this.activeQuickLink = 'my'

      const savedNav = localStorage.getItem(this.localStorage_key + '_nav')
      if (savedNav) {
        try {
          const { selectedEntityType, navigationPath } = JSON.parse(savedNav)
          this.selectedEntityType = selectedEntityType
          this.navigationPath = navigationPath || []
          const lastItem = this.navigationPath[this.navigationPath.length - 1]
          if (lastItem || selectedEntityType) {
            await this.fetchChildren(lastItem?.id || null, lastItem ? null : selectedEntityType)
          } else if (this.activeQuickLink === 'my') {
            await this.navigateToMyPractices()
          }
        } catch {
          this.selectedEntityType = null
          this.navigationPath = []
        }
      } else if (savedQuick === 'my') {
        await this.navigateToMyPractices()
      }
    } finally {
      this.loading = false
    }
  }
}
</script>
